/** @jsx jsx */
import { jsx, useTheme } from '@emotion/react'
import {
    AppState,
    AuthenticationState,
    ConsentLevel,
    Product,
    ScriptLoadResult,
    useScriptWithConsent,
} from '@news-mono/web-common'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { ContentSeparator } from '../content/ContentSeparator/ContentSeparator.styled'
import { metrics } from '../__styling/settings/metrics'
import { calcRem } from '../__styling/style-functions/calc-rem'
import { CoralProps } from './Coral'
import { CoralEventStream, handleCoralEvents } from './coral-event-stream'

declare const Coral: CoralScript

interface CoralStreamEmbedProps {
    storyID?: string
    storyURL?: string
    commentID?: string
    rootURL?: string
    id?: string
    autoRender?: boolean
    accessToken?: string
    enableDeprecatedEvents?: boolean
    customCSSURL?: string

    /** Allow setting className of body tag inside iframe */
    containerClassName?: string
    events?: (eventStream: CoralEventStream) => void
}

interface CoralScript {
    createStreamEmbed: (props: CoralStreamEmbedProps) => void
}

function checkCoral() {
    return typeof Coral !== 'undefined' && Coral && Coral.createStreamEmbed
}

export interface CoralCommentsProps extends Omit<CoralProps, 'userSettings'> {
    coralScriptUrl: string
    hostName: string
    useContentSeperator: boolean
    canComment?: boolean
}

export const CoralComments: React.FC<CoralCommentsProps> = ({
    coralScriptUrl,
    storyID,
    storyURL,
    horizontalSpacing,
    onCommentEvent: onEvent,
    hostName,
    useContentSeperator,
    canComment = true,
}) => {
    const { kind } = useTheme()
    const [result] = useScriptWithConsent({
        consentRequiredLevel: ConsentLevel.Essential | ConsentLevel.Analytics,
        consentMet: { src: `${coralScriptUrl}/assets/js/embed.js` },
    })

    const [streamLoaded, updateStreamLoaded] = useState(false)

    const authState = useSelector((state: AppState) => state.authentication)

    const renditionType = useSelector(
        (state: AppState) => state.render.renditionType,
    )

    const { coralToken, hasAttemptedValidation, checkingTokenState } = authState

    React.useEffect(() => {
        const loadStream =
            result === ScriptLoadResult.Success &&
            checkCoral() &&
            ((coralToken && kind === Product.TheWest) ||
                kind === Product.PerthNow ||
                kind === Product.TheNightly) &&
            hasAttemptedValidation &&
            !checkingTokenState

        // This allows us to insert some extra styles into the styleSheet that is used to style the Coral embed.
        const viewOnly =
            (!coralToken &&
                (kind === Product.PerthNow || kind === Product.TheNightly)) ||
            !canComment

        if (renditionType === 'app') {
            const redactedAuthState: AuthenticationState = {
                ...authState,
                userEmail: 'redacted',
                userName: 'redacted',
                wanUserId:
                    authState.wanUserId !== 'app-bad-token'
                        ? 'redacted'
                        : authState.wanUserId,
            }

            console.info('INC-251 hasDuplicates', {
                wanUserId: redactedAuthState.wanUserId,
                coralToken: redactedAuthState.coralToken,
                canLoadStream: loadStream,
            })
        }

        if (loadStream) {
            Coral.createStreamEmbed({
                id: 'coral_thread',
                autoRender: true,
                rootURL: coralScriptUrl,
                accessToken: coralToken,
                containerClassName: `${kind}-comments`,
                customCSSURL: `${hostName}/coral.css${
                    viewOnly ? '?viewOnly=true' : ''
                }`,
                storyID,
                storyURL,
                events: function (eventStream) {
                    handleCoralEvents(eventStream, onEvent)
                },
            })
            updateStreamLoaded(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        result,
        coralScriptUrl,
        kind,
        storyID,
        storyURL,
        onEvent,
        coralToken,
        hasAttemptedValidation,
        checkingTokenState,
    ])

    return (
        <React.Fragment>
            <div
                id="coral_thread"
                className="hide-print"
                css={{
                    margin: `0 ${
                        horizontalSpacing &&
                        calcRem(metrics.thewest.margins[horizontalSpacing])
                    }`,
                }}
            />
            {useContentSeperator && streamLoaded && (
                <ContentSeparator verticalBottomSpacing="lg" />
            )}
        </React.Fragment>
    )
}

export default CoralComments
